import React, { useEffect, useMemo, useRef, useState } from "react";
import JSZip from "jszip";
import { useApi } from "../context/ApiContext";
import Button from "./ui/Button";
import ProgressBar from "./ui/ProgressBar";
import "./ZipIndexer.css";

/**
 * Indexation d'une ARCHIVE : un .zip dont chaque dossier nomme sa langue, et
 * son pays quand il compte.
 *
 * DEUX FORMES DE NOM DE DOSSIER
 * -----------------------------
 *  - une LANGUE seule (« en », « fr », « esal »…) : contenu international,
 *    sans pays précis (le filtre pays reste vide) ;
 *  - un SLUG PAYS complet (« usa_en », « usa_esal », « canada_fr », « france_fr »…) :
 *    le dossier porte alors le pays ET la langue. Le slug est exactement celui
 *    qu'utilise le site pour filtrer la mémoire, donc la correspondance est
 *    parfaite. La règle : « <n'importe quoi>_<langue connue> » est reconnu comme
 *    un slug pays ; le pays est le nom de dossier entier, la langue son suffixe.
 *
 * Un nom qui n'est ni une langue ni ce motif (« taoofds ») est signalé et laissé
 * de côté. Le pays venant du dossier, il n'y a pas de champ « Pays » commun.
 *
 * POURQUOI CE MODE, ALORS QUE L'ANCIEN ZIP AVAIT ÉTÉ RETIRÉ
 * ---------------------------------------------------------
 * L'ancien traitait un lot à plat, en tâche de fond, sans rien vérifier ni
 * rendre de compte. Ici la structure est le contrat, et tout est piloté par le
 * navigateur — mêmes prévisualisation et envoi par lots que l'onglet PDF, donc
 * même barre d'avancement réelle.
 */

const TAILLE_LOT = 20;

/** Fichiers d'habillage d'archive à ignorer (macOS, fichiers cachés). */
const isTechnique = (chemin) =>
    chemin.startsWith("__MACOSX/") ||
    chemin.split("/").some((seg) => seg.startsWith("."));

export default function ZipIndexer() {
    const { apiFetch } = useApi();

    const [zipFile, setZipFile] = useState(null);
    const [collection, setCollection] = useState(localStorage.getItem("zip_collection") || "");
    const [support, setSupport] = useState(localStorage.getItem("zip_support") || "brochure");
    const [campaign, setCampaign] = useState(
        localStorage.getItem("zip_campaign") || String(new Date().getFullYear())
    );

    const [collections, setCollections] = useState([]);
    const [supports, setSupports] = useState([]);
    const [languages, setLanguages] = useState([]);

    const [plan, setPlan] = useState(null); // { reconnus: [{folder, lang, country, fichiers}], ignores: [{dossier, nb}] }
    const [analyse, setAnalyse] = useState(false);
    const [error, setError] = useState("");

    const [phase, setPhase] = useState("idle"); // idle | running | done | error
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [message, setMessage] = useState("");
    const [rapport, setRapport] = useState(null); // { [folder]: {fichiers, fichiersTotal, segments, echecs, erreurs} }
    const interrompu = useRef(false);

    useEffect(() => {
        apiFetch("/collections").then(r => r.json()).then(j => setCollections(j.data || []));
        apiFetch("/supports").then(r => r.json()).then(j => setSupports(j.data || []));
        apiFetch("/languages").then(r => r.json()).then(j => setLanguages(j.data || []));
    }, [apiFetch]);

    useEffect(() => {
        if (phase !== "running") {
            return undefined;
        }
        const garde = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
        window.addEventListener("beforeunload", garde);
        return () => window.removeEventListener("beforeunload", garde);
    }, [phase]);

    const languesConnues = useMemo(
        () => new Set(languages.map((l) => l.toUpperCase())),
        [languages]
    );

    /**
     * Interprète un nom de dossier : { lang, country } ou null si non reconnu.
     *  - langue seule       → { lang, country: "" }         (international)
     *  - <pays>_<langue>    → { lang, country: nomComplet }  (pays précis)
     */
    const interpreter = (dossier) => {
        const code = dossier.toUpperCase();
        if (languesConnues.has(code)) {
            return { lang: code, country: "" };
        }
        const i = dossier.lastIndexOf("_");
        if (i > 0) {
            const suffixe = dossier.slice(i + 1).toUpperCase();
            if (languesConnues.has(suffixe)) {
                return { lang: suffixe, country: dossier.toLowerCase() };
            }
        }
        return null;
    };

    const remember = (cle, setter) => (e) => {
        const value = e.target.value;
        setter(value);
        localStorage.setItem(`zip_${cle}`, value);
    };

    /** Lit le zip et construit le plan, sans rien écrire. */
    const analyser = async () => {
        setError("");
        setPlan(null);
        setRapport(null);
        setPhase("idle");

        if (!zipFile) { setError("Sélectionnez une archive .zip."); return; }
        if (collection === "") { setError("La collection est obligatoire."); return; }
        if (support === "") { setError("Le support est obligatoire."); return; }
        if (languesConnues.size === 0) { setError("Liste des langues indisponible."); return; }

        setAnalyse(true);
        try {
            const zip = await JSZip.loadAsync(zipFile);

            // Regroupe les PDF par dossier PARENT immédiat. Un éventuel dossier
            // d'enrobage (zip d'un dossier) ne gêne pas : on ne regarde que le
            // parent direct du fichier.
            const parDossier = new Map();
            zip.forEach((chemin, entry) => {
                if (entry.dir || isTechnique(chemin)) { return; }
                if (!/\.pdf$/i.test(chemin)) { return; }
                const parts = chemin.split("/").filter(Boolean);
                const dossier = parts.length >= 2 ? parts[parts.length - 2] : "(racine)";
                if (!parDossier.has(dossier)) { parDossier.set(dossier, []); }
                parDossier.get(dossier).push({ name: parts[parts.length - 1], entry });
            });

            if (parDossier.size === 0) {
                setError("Aucun PDF trouvé dans l'archive.");
                return;
            }

            const reconnus = [];
            const ignores = [];
            for (const [dossier, fichiers] of parDossier) {
                const interp = interpreter(dossier);
                if (interp) {
                    const blobs = [];
                    for (const f of fichiers) {
                        blobs.push({ name: f.name, blob: await f.entry.async("blob") });
                    }
                    reconnus.push({ folder: dossier, lang: interp.lang, country: interp.country, fichiers: blobs });
                } else {
                    ignores.push({ dossier, nb: fichiers.length });
                }
            }

            reconnus.sort((a, b) => a.folder.localeCompare(b.folder));
            setPlan({ reconnus, ignores });

            if (reconnus.length === 0) {
                setError("Aucun dossier reconnu. Nommez chaque dossier par sa langue (en, fr, esal…) ou par un slug pays (usa_en, canada_fr, france_fr…).");
            }
        } catch (err) {
            setError("Archive illisible : " + err.message);
        } finally {
            setAnalyse(false);
        }
    };

    /** Prévisualise puis indexe UN fichier ; renvoie {segments, echecs}. */
    const indexerFichier = async (blob, langue, pays) => {
        const fd = new FormData();
        fd.append("file", blob, "doc.pdf");
        fd.append("language", langue);

        const prev = await apiFetch("/vectors/preview", { method: "POST", body: fd }).then(r => r.json());
        if (!prev.success || !Array.isArray(prev.segments)) {
            throw new Error(prev.error || "découpage impossible");
        }
        const segments = Array.from(new Set(prev.segments.map(s => s.trim()).filter(Boolean)));

        let ecrits = 0;
        let echecs = 0;
        for (let i = 0; i < segments.length; i += TAILLE_LOT) {
            if (interrompu.current) { break; }
            const lot = segments.slice(i, i + TAILLE_LOT);
            const res = await apiFetch("/vectors/segments", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ segments: lot, collection, language: langue, support, country: pays, campaign }),
            }).then(r => r.json());
            if (!res.success) { throw new Error(res.error || "indexation refusée"); }
            ecrits += res.written || 0;
            echecs += res.failed || 0;
        }
        return { segments: ecrits, echecs };
    };

    /** Parcourt tout le plan, dossier par dossier, fichier par fichier. */
    const indexer = async () => {
        if (!plan || plan.reconnus.length === 0) { return; }

        interrompu.current = false;
        setError("");
        setMessage("");
        setPhase("running");
        setRapport(null);

        const totalFichiers = plan.reconnus.reduce((n, d) => n + d.fichiers.length, 0);
        setProgress({ done: 0, total: totalFichiers });

        const bilan = {};
        let faits = 0;

        for (const dossier of plan.reconnus) {
            let segTotal = 0;
            let echecsTotal = 0;
            let fichiersOk = 0;
            const erreurs = [];

            for (const f of dossier.fichiers) {
                if (interrompu.current) { break; }
                try {
                    const r = await indexerFichier(f.blob, dossier.lang, dossier.country);
                    segTotal += r.segments;
                    echecsTotal += r.echecs;
                    fichiersOk += 1;
                } catch (err) {
                    erreurs.push(`${f.name} : ${err.message}`);
                }
                faits += 1;
                setProgress({ done: faits, total: totalFichiers });
            }

            bilan[dossier.folder] = {
                fichiers: fichiersOk,
                fichiersTotal: dossier.fichiers.length,
                segments: segTotal,
                echecs: echecsTotal,
                erreurs,
            };

            if (interrompu.current) { break; }
        }

        setRapport(bilan);
        if (interrompu.current) {
            setPhase("error");
            setMessage(`Interrompu : ${faits}/${totalFichiers} fichier(s) traités, les segments déjà indexés sont conservés.`);
        } else {
            const seg = Object.values(bilan).reduce((n, b) => n + b.segments, 0);
            setPhase("done");
            setMessage(`${faits} fichier(s), ${seg} segment(s) indexés.`);
        }
    };

    const enCours = phase === "running";

    return (
        <form className="form-grid" onSubmit={(e) => e.preventDefault()}>
            <p className="form-hint">
                Déposez un <strong>.zip</strong> dont chaque dossier porte&nbsp;:
                soit une <strong>langue</strong> (<code>en</code>, <code>fr</code>, <code>esal</code>…)
                pour du contenu international, soit un <strong>slug pays</strong>
                (<code>usa_en</code>, <code>usa_esal</code>, <code>canada_fr</code>, <code>france_fr</code>…)
                quand le pays compte. Un dossier au nom non reconnu est signalé et laissé de côté.
            </p>

            <div className="field field--wide">
                <label htmlFor="zip-file">Archive (.zip)</label>
                <input id="zip-file" type="file" accept=".zip" disabled={enCours}
                       onChange={e => { setZipFile(e.target.files[0] || null); setPlan(null); setRapport(null); setPhase("idle"); }} />
            </div>

            <div className="field">
                <label htmlFor="zip-collection">Collection</label>
                <select id="zip-collection" value={collection} disabled={enCours}
                        onChange={remember("collection", setCollection)}>
                    <option value="">—</option>
                    {collections.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>

            <div className="field">
                <label htmlFor="zip-support">Support</label>
                <select id="zip-support" value={support} disabled={enCours}
                        onChange={remember("support", setSupport)}>
                    <option value="">—</option>
                    {supports.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>

            <div className="field">
                <label htmlFor="zip-campaign">Campagne</label>
                <input id="zip-campaign" type="text" placeholder="2026" value={campaign}
                       disabled={enCours} onChange={remember("campaign", setCampaign)} />
                <span className="field__hint">Commune à toute l'archive.</span>
            </div>

            {error && <p className="notice notice--error field--wide">{error}</p>}

            {plan && (
                <div className="zip-plan field--wide">
                    {plan.reconnus.length > 0 && (
                        <div className="zip-plan__block">
                            <h4>Dossiers reconnus</h4>
                            <ul>
                                {plan.reconnus.map(d => {
                                    const b = rapport && rapport[d.folder];
                                    return (
                                        <li key={d.folder}>
                                            <span className="tag tag--brand">{d.lang}</span>
                                            {d.country
                                                ? <span className="tag">{d.country}</span>
                                                : <span className="zip-plan__intl">international</span>}
                                            {d.fichiers.length} fichier(s)
                                            {b && (
                                                <span className="zip-plan__result">
                                                    → {b.fichiers}/{b.fichiersTotal} traité(s), {b.segments} segment(s)
                                                    {b.echecs > 0 && <>, {b.echecs} en échec</>}
                                                    {b.erreurs.length > 0 && (
                                                        <span className="zip-plan__err"> · {b.erreurs.join(" ; ")}</span>
                                                    )}
                                                </span>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}

                    {plan.ignores.length > 0 && (
                        <div className="zip-plan__block zip-plan__block--warn">
                            <h4>Dossiers ignorés (nom non reconnu)</h4>
                            <ul>
                                {plan.ignores.map(d => (
                                    <li key={d.dossier}>
                                        <span className="tag">{d.dossier}</span>
                                        {d.nb} fichier(s) — non pris en compte
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            <ProgressBar
                written={progress.done}
                failed={0}
                total={progress.total}
                state={phase}
                message={message}
                unit="fichier"
                onAbort={() => { interrompu.current = true; }}
            />

            <div className="form-actions">
                <Button type="button" onClick={analyser} disabled={analyse || enCours}>
                    {analyse ? "Lecture…" : "Analyser l'archive"}
                </Button>
                {plan && plan.reconnus.length > 0 && (
                    <Button type="button" variant="secondary" onClick={indexer} disabled={enCours}>
                        Indexer {plan.reconnus.reduce((n, d) => n + d.fichiers.length, 0)} fichier(s)
                    </Button>
                )}
            </div>
        </form>
    );
}
